// Quiz Timer Component - Countdown timer for quiz time limit
import React from "react";
import { useQuizTimer } from "../../../hooks/useQuizTimer";

interface QuizTimerProps {
  initialTime: number; // in seconds
  onTimeUp: () => void;
  isRunning?: boolean;
  className?: string;
  quizId?: string;
  attemptId?: string;
}

const QuizTimer: React.FC<QuizTimerProps> = ({
  initialTime,
  onTimeUp,
  isRunning = true,
  className = "",
  quizId,
  attemptId,
}) => {
  const {
    timeRemaining,
    formattedTime,
    start,
    timeStatus,
    percentageRemaining,
    isTimeUp,
  } = useQuizTimer(initialTime, onTimeUp, quizId, attemptId);

  React.useEffect(() => {
    if (isRunning) {
      start();
    }
  }, [isRunning, start]);

  const getTimerColor = () => {
    switch (timeStatus) {
      case "critical":
        return "bg-red-100 border-red-500 text-red-800";
      case "warning":
        return "bg-yellow-100 border-yellow-500 text-yellow-800";
      default:
        return "bg-blue-100 border-blue-500 text-blue-800";
    }
  };

  const getProgressColor = () => {
    switch (timeStatus) {
      case "critical":
        return "bg-red-500";
      case "warning":
        return "bg-yellow-500";
      default:
        return "bg-blue-500";
    }
  };

  if (isTimeUp) {
    return (
      <div
        className={`bg-red-100 border-2 border-red-500 rounded-lg p-2 sm:p-4 text-center ${className}`}
      >
        <div className="text-red-800">
          <div className="text-lg sm:text-2xl font-bold mb-1">
            ⏰ TIME'S UP!
          </div>
          <div className="text-xs sm:text-sm">
            Quiz has been automatically submitted
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border-2 rounded-lg p-2 text-center transition-all duration-300 ${getTimerColor()} ${className}`}
    >
      <div className="flex items-center justify-center space-x-1 sm:space-x-2">
        <span className="text-sm sm:text-lg">⏱️</span>
        <span className="text-xs sm:text-sm font-medium hidden sm:inline">
          Time Remaining
        </span>
      </div>

      <div className="text-xs sm:text-lg font-bold font-mono mb-1">
        {formattedTime}
      </div>

      {/* Progress Bar - Hidden on mobile */}
      <div className="hidden sm:block w-full bg-gray-200 rounded-full h-2 mb-2">
        <div
          className={`h-2 rounded-full transition-all duration-1000 ${getProgressColor()}`}
          style={{ width: `${percentageRemaining}%` }}
        />
      </div>

      {/* <div className="text-xs opacity-75 hidden sm:block">
        {Math.round(percentageRemaining)}% remaining
      </div> */}

      {timeStatus === "critical" && (
        <div className="mt-1 sm:mt-2 text-xs font-medium animate-pulse">
          ⚠️ Less than 10% time remaining!
        </div>
      )}
    </div>
  );
};

export default QuizTimer;
